import React from "react"
import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import type { Organization } from "@/models/organization"
import type { UpdateBillInput } from "@/services/bill/use-update-bill"
import type { Bill, BillGetOneResponse } from "@/services/core-api/types"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"

interface UseBillLockToggleOptions {
  bill: Bill
  organizationId: Organization["id"]
  updateBillMutateAsync: (input: UpdateBillInput) => Promise<BillGetOneResponse>
}

export const useBillLockToggle = ({
  bill,
  organizationId,
  updateBillMutateAsync,
}: UseBillLockToggleOptions) => {
  const { t } = useTranslation("common")
  const { setNotification } = useNotification()
  const [lockToggleUiValue, setLockToggleUiValue] = React.useState<
    boolean | null
  >(null)

  const isBillLocked = bill.is_locked
  const isLockToggleSyncing =
    lockToggleUiValue !== null && lockToggleUiValue !== isBillLocked
  const displayedLockedValue = isLockToggleSyncing
    ? lockToggleUiValue
    : isBillLocked

  const handleLockChange = async (nextLocked: boolean) => {
    if (nextLocked === displayedLockedValue) {
      return
    }

    setLockToggleUiValue(nextLocked)

    try {
      await updateBillMutateAsync({
        billId: bill.id,
        organizationId,
        updates: {
          is_locked: nextLocked,
        },
      })
    } catch (error: unknown) {
      setLockToggleUiValue(null)
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("editBill.lockUpdateError")
        ),
        type: "error",
      })
    }
  }

  const clearLockToggleUiValue = () => {
    setLockToggleUiValue(null)
  }

  return {
    clearLockToggleUiValue,
    displayedLockedValue,
    handleLockChange,
    isLockToggleSyncing,
  }
}
